/**
 * Utility function to create score notifications
 * Creates in-app notifications for score awards/deductions
 */

import { supabase } from "@/lib/supabase/client";
import { DEFAULT_LOTTIE_BY_TYPE } from "./lottieAnimations";
import { DEFAULT_THEME_BY_TYPE } from "./themes";

interface CreateScoreNotificationParams {
  /** Team ID that received points */
  teamId: string;
  /** Team name */
  teamName: string;
  /** Points awarded (negative for deductions) */
  points: number;
  /** Reason for the points */
  reason: string;
  /** User ID who awarded the points */
  awardedBy: string;
  /** Specific user who earned the points (for individual awards) */
  awardedToUserId?: string;
  /** Name of user who earned points */
  awardedToUserName?: string;
  /** Active weekend ID */
  weekendId?: string;
}

/**
 * Create score notifications for team members
 * - If individual award: notifies that user + all teammates
 * - If team award: notifies all team members
 */
export const createScoreNotifications = async ({
  teamId,
  teamName,
  points,
  reason,
  awardedBy,
  awardedToUserId,
  awardedToUserName,
  weekendId,
}: CreateScoreNotificationParams): Promise<void> => {
  try {
    // Get team members
    const { data: teamMembers, error: membersError } = await supabase
      .from("team_members")
      .select("user_id")
      .eq("team_id", teamId);

    if (membersError) {
      console.error("Error fetching team members:", membersError);
      return;
    }

    if (!teamMembers || teamMembers.length === 0) {
      console.log("No team members found for notifications");
      return;
    }

    const isAdd = points > 0;
    const notificationType = isAdd ? "score_positive" : "score_negative";
    
    // Different defaults for gaining vs losing points
    let themeId = isAdd 
      ? DEFAULT_THEME_BY_TYPE.score_positive 
      : DEFAULT_THEME_BY_TYPE.score_negative;
    let lottieAnimation = isAdd 
      ? DEFAULT_LOTTIE_BY_TYPE.score_positive 
      : DEFAULT_LOTTIE_BY_TYPE.score_negative;
    let titleTemplate = isAdd ? "Gefeliciteerd!" : "Oeps...";
    
    // Check for customized notification settings
    if (weekendId) {
      const { data: settings } = await supabase
        .from("notification_type_settings")
        .select("theme_id, lottie_animation, title_template, is_enabled")
        .eq("weekend_id", weekendId)
        .eq("notification_type", notificationType)
        .single();
      
      if (settings && settings.is_enabled !== false) {
        if (settings.theme_id) themeId = settings.theme_id;
        if (settings.lottie_animation) lottieAnimation = settings.lottie_animation;
        if (settings.title_template) titleTemplate = settings.title_template;
      }
    }
    const absPoints = Math.abs(points);

    // Create notifications for each team member
    const notifications = teamMembers
      .filter(member => member.user_id !== awardedBy) // Don't notify the admin who awarded
      .map(member => {
        const isAwardedUser = awardedToUserId && member.user_id === awardedToUserId;
        
        // Different messages based on who is receiving
        let title = titleTemplate;
        let body: string;
        
        if (isAwardedUser) {
          // The user who earned the points
          body = isAdd
            ? `Je hebt ${absPoints} punten verdiend voor ${teamName}!\n${reason}`
            : `${absPoints} punten afgetrokken van ${teamName}.\n${reason}`;
        } else if (awardedToUserId && awardedToUserName) {
          // Teammates seeing someone else earned points
          body = isAdd
            ? `${awardedToUserName} heeft ${absPoints} punten verdiend voor ${teamName}!\n${reason}`
            : `${absPoints} punten afgetrokken van ${teamName} door ${awardedToUserName}.\n${reason}`;
        } else {
          // Team points (no specific user)
          body = isAdd
            ? `${teamName} heeft ${absPoints} punten ontvangen!\n${reason}`
            : `${absPoints} punten afgetrokken van ${teamName}.\n${reason}`;
        }

        return {
          user_id: member.user_id,
          type: "in_app",
          notification_type: notificationType,
          title,
          body,
          lottie_animation: lottieAnimation,
          is_read: false,
          sender_id: awardedBy,
          weekend_id: weekendId,
          action_data: {
            points: points,
            team_id: teamId,
            team_name: teamName,
            reason: reason,
            awarded_to_user_id: awardedToUserId,
            awarded_to_user_name: awardedToUserName,
            theme_id: themeId,
          },
        };
      });

    if (notifications.length === 0) {
      console.log("No notifications to create (only admin in team)");
      return;
    }

    // Batch insert notifications
    const { error: insertError } = await supabase
      .from("notifications")
      .insert(notifications);

    if (insertError) {
      console.error("Error creating notifications:", insertError);
      return;
    }

    console.log(`✅ Created ${notifications.length} score notifications`);
  } catch (error) {
    console.error("Error in createScoreNotifications:", error);
  }
};

export default createScoreNotifications;


